import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { isInstalledConnection } from 'app/connections/types'
import {
  FacebookAdAccount,
  FacebookConnection,
  FacebookConnectionConfig,
  InstalledFacebookConnection,
} from 'app/connections/types/facebook'

import { DropDown } from 'components'

import { Authorization } from '../../authorization'
import styles from '../Settings.module.scss'
import { AccountsSettings, DangerZone, SettingsSection } from '../components'

interface MetaSettingsProps {
  connection: FacebookConnection | InstalledFacebookConnection
  connectionConfig: FacebookConnectionConfig
  onConnectionConfigUpdate: (key: string, value: FacebookAdAccount[] | string) => void
  onConnectionConfigValidationUpdate: (isValidConfig: boolean) => void
}

export const MetaSettings = ({
  connection,
  connectionConfig,
  onConnectionConfigUpdate,
  onConnectionConfigValidationUpdate,
}: MetaSettingsProps) => {
  const { t } = useTranslation('connections')

  const updateConfig = (
    key: Extract<keyof FacebookConnectionConfig, string>,
    value: FacebookAdAccount[] | string,
  ) => {
    const nextConfig = { ...connectionConfig, [key]: value }

    onConnectionConfigUpdate(key, value)
    onConnectionConfigValidationUpdate(
      nextConfig.accounts.length > 0 && !!nextConfig.pixelId && !!nextConfig.adId,
    )
  }

  const removeAccount = (account: FacebookAdAccount) =>
    updateConfig(
      'accounts',
      connectionConfig.accounts.filter(({ id }) => id !== account.id),
    )

  const pixelIds = connectionConfig.pixelId ? [connectionConfig.pixelId] : []
  const adIds = connectionConfig.adId ? [connectionConfig.adId] : []

  useEffect(() => {
    onConnectionConfigValidationUpdate(
      connectionConfig.accounts.length > 0 && !!connectionConfig.pixelId && !!connectionConfig.adId,
    )
  }, [connectionConfig, onConnectionConfigValidationUpdate])

  return (
    <>
      <SettingsSection
        contentClassName={styles.basicCard}
        description={t('setup.basics.description')}
        title={t('setup.basics.title')}
      >
        <Authorization connection={connection} />
      </SettingsSection>
      <AccountsSettings
        accounts={connectionConfig.accounts}
        connectionName={connection.name}
        onRemove={removeAccount}
      />
      <SettingsSection
        contentClassName={styles.idsCard}
        description={t('manage.meta.ids.description')}
        title={t('manage.meta.ids.title')}
      >
        <label>
          <span>{t('setup.facebook.config.pixelId.title')}</span>
          <DropDown
            data={pixelIds}
            onSelect={(pixelId) => updateConfig('pixelId', pixelId)}
            placeholder={t('setup.facebook.config.pixelId.title')}
            selected={connectionConfig.pixelId || null}
          />
        </label>
        <label>
          <span>{t('setup.facebook.config.adId.title')}</span>
          <DropDown
            data={adIds}
            onSelect={(adId) => updateConfig('adId', adId)}
            placeholder={t('setup.facebook.config.adId.title')}
            selected={connectionConfig.adId || null}
          />
        </label>
      </SettingsSection>
      {isInstalledConnection(connection) && <DangerZone connection={connection} />}
    </>
  )
}
